import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AuthCallback } from "@/components/auth/AuthCallback";
import { LangProvider } from "@/context/LangContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ProgressProvider } from "@/context/ProgressContext";
import { QariProvider } from "@/context/QariContext";
import { AudiobookProvider } from "@/context/AudiobookContext";
import { MurottalProvider } from "@/context/MurottalContext";
import { Layout } from "@/components/Layout";
import { DataSaverProvider } from "@/context/DataSaverContext";
import { DataSaverSuggest } from "@/components/DataSaverSuggest";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import { Loader2 } from "lucide-react";

// Route-level code splitting: only the Dashboard & Auth pages ship in the main bundle.
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const Guide = lazy(() => import("@/pages/Guide"));
const Prayers = lazy(() => import("@/pages/Prayers"));
const MapPage = lazy(() => import("@/pages/MapPage"));
const PrayerTimesPage = lazy(() => import("@/pages/PrayerTimesPage"));
const HealthProgramPage = lazy(() => import("@/pages/HealthProgramPage"));
const ItineraryPage = lazy(() => import("@/pages/ItineraryPage"));
const QuranPage = lazy(() => import("@/pages/QuranPage"));
const ZiyarahPage = lazy(() => import("@/pages/ZiyarahPage"));
const SholatJenazahPage = lazy(() => import("@/pages/SholatJenazahPage"));
const BaqiPage = lazy(() => import("@/pages/BaqiPage"));
const MasjidNabawiPage = lazy(() => import("@/pages/MasjidNabawiPage"));
const SholatSunnahPage = lazy(() => import("@/pages/SholatSunnahPage"));
const MasjidilHaramPage = lazy(() => import("@/pages/MasjidilHaramPage"));
const KeutamaanPage = lazy(() => import("@/pages/KeutamaanPage"));
const FaqPage = lazy(() => import("@/pages/FaqPage"));
const HealthTipsPage = lazy(() => import("@/pages/HealthTipsPage"));
const PrepPage = lazy(() => import("@/pages/PrepPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const PackagesPage = lazy(() => import("@/pages/PackagesPage"));
const Tasbih = lazy(() => import("@/pages/Tasbih"));
const ZikirUtamaPage = lazy(() => import("@/pages/ZikirUtamaPage"));
const AdhkarPage = lazy(() => import("@/pages/AdhkarPage"));
const UmrahMandiriPage = lazy(() => import("@/pages/UmrahMandiriPage"));
const AppsPage = lazy(() => import("@/pages/AppsPage"));
const BelajarPage = lazy(() => import("@/pages/BelajarPage"));
const HajjTimelinePage = lazy(() => import("@/pages/HajjTimelinePage"));
const ManasikRefPage = lazy(() => import("@/pages/ManasikRefPage"));
const VideoGuides = lazy(() => import("@/pages/VideoGuides"));
const Profile = lazy(() => import("@/pages/Profile"));
const DownloadsPage = lazy(() => import("@/pages/DownloadsPage"));
const InstallAppPage = lazy(() => import("@/pages/InstallAppPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const SupportPage = lazy(() => import("@/pages/SupportPage"));

const Splash = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/auth" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to="/" replace />;
  return children;
};

function AppRoutes() {
  const location = useLocation(); // hash must come from the router location (reactive), not window.location
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
          <Suspense fallback={<Splash />}>
            <Routes>
              <Route path="/auth" element={<PublicOnly><AuthPage /></PublicOnly>} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/install" element={<InstallAppPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route
                element={
                  <ProtectedRoute>
                    <ProgressProvider>
                      <QariProvider>
                        <AudiobookProvider>
                          <MurottalProvider>
                            <Layout />
                          </MurottalProvider>
                        </AudiobookProvider>
                      </QariProvider>
                    </ProgressProvider>
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Dashboard />} />
                <Route path="/guide" element={<Guide />} />
                <Route path="/prayers" element={<Prayers />} />
                <Route path="/map" element={<MapPage />} />
                <Route path="/times" element={<PrayerTimesPage />} />
                <Route path="/tasbih" element={<Tasbih />} />
                <Route path="/zikir" element={<ZikirUtamaPage />} />
                <Route path="/pagi-petang" element={<AdhkarPage />} />
                <Route path="/umrah-mandiri" element={<UmrahMandiriPage />} />
                <Route path="/apps" element={<AppsPage />} />
                <Route path="/belajar" element={<BelajarPage />} />
                <Route path="/haji-timeline" element={<HajjTimelinePage />} />
                <Route path="/manasik-referensi" element={<ManasikRefPage />} />
                <Route path="/videos" element={<VideoGuides />} />
                <Route path="/quran" element={<QuranPage />} />
                <Route path="/ziyarah" element={<ZiyarahPage />} />
                <Route path="/jenazah" element={<SholatJenazahPage />} />
                <Route path="/baqi" element={<BaqiPage />} />
                <Route path="/nabawi" element={<MasjidNabawiPage />} />
                <Route path="/sunnah" element={<SholatSunnahPage />} />
                <Route path="/haram" element={<MasjidilHaramPage />} />
                <Route path="/keutamaan" element={<KeutamaanPage />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/health" element={<HealthTipsPage />} />
                <Route path="/sehat" element={<HealthProgramPage />} />
                <Route path="/itinerary" element={<ItineraryPage />} />
                <Route path="/prep" element={<PrepPage />} />
                <Route path="/packages" element={<PackagesPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/downloads" element={<DownloadsPage />} />
                <Route path="/support" element={<SupportPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <LangProvider>
        <AuthProvider>
          <DataSaverProvider>
            <BrowserRouter>
              <AppRoutes />
              <DataSaverSuggest />
            </BrowserRouter>
            <Toaster position="top-center" richColors />
          </DataSaverProvider>
        </AuthProvider>
      </LangProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
